// Cloud login session (OIDC authorization-code + PKCE via the hosted UI).
//
// The viewer owns the login flow for the whole origin: the dashboard (a
// separate static app on the same origin) bounces here with ?return=<path>
// when it hits a 401 and is sent back after sign-in. The access token lives
// in oidc-client-ts's localStorage session AND is mirrored into a same-origin
// `at` cookie (Secure, SameSite=Strict) so header-less requests — <img>
// thumbnails, EventSource — authenticate too. Silent renew (refresh token)
// keeps both fresh.
//
// With no auth config (local mode) this module is a no-op.

import { createRoot } from 'react-dom/client';
import { UserManager, WebStorageStateStore, type User } from 'oidc-client-ts';
import { getConfig } from '../config/runtimeConfig';
import { currentSession, type SessionProbe } from './passwordSession';
import { AuthScreens, type Screen } from './AuthScreens';

const COOKIE = 'at';

function setSessionCookie(token: string): void {
  document.cookie = `${COOKIE}=${token}; path=/; secure; samesite=strict`;
}

export function safeReturnPath(raw: string | null | undefined): string | null {
  // Only same-origin paths. Decided by PARSING, not by prefix matching: the
  // string is handed to location.replace(), which resolves it with the URL
  // parser, and that parser treats a backslash as a solidus for special
  // schemes. So `/\evil.com` starts with a single slash, passes any
  // startsWith('//') test, and still navigates to https://evil.com — an open
  // redirect on the trusted origin, which is exactly what a phishing link
  // wants. Resolving here with the same algorithm that will resolve it later
  // closes that and any other parser quirk by construction.
  if (!raw) return null;
  try {
    const u = new URL(raw, window.location.origin);
    if (u.origin !== window.location.origin) return null;
    return u.pathname + u.search + u.hash;
  } catch {
    return null;
  }
}

/** Never-resolving promise: we are navigating away; don't render the app. */
function navigating(): Promise<never> {
  return new Promise<never>(() => {});
}

/**
 * Resolves when there is a valid session (cookie set), redirects to the
 * hosted UI when there isn't (and then never resolves — the page unloads).
 */
export async function ensureSignedIn(early?: SessionProbe): Promise<void> {
  // No "if (!auth) return" here any more. That line is what let the app carry
  // on unauthenticated whenever config.js had no `auth` key — it returned
  // straight past the sign-in gate, and the worklist then rendered its 401s as
  // an empty list. auth is non-nullable now, so there is nothing to skip.
  const auth = getConfig().auth;

  // This hub holds the credentials: there is nowhere to redirect to, so the
  // gate renders a form and resolves once it succeeds.
  if (auth.mode === 'password') return ensurePasswordSession(early);

  const mgr = new UserManager({
    authority: auth.authority,
    client_id: auth.clientId,
    redirect_uri: `${window.location.origin}/`,
    response_type: 'code',
    scope: 'openid email profile',
    automaticSilentRenew: true,
    userStore: new WebStorageStateStore({ store: window.localStorage }),
  });
  mgr.events.addUserLoaded((u: User) => {
    if (u.access_token) setSessionCookie(u.access_token);
  });

  const params = new URLSearchParams(window.location.search);

  // Returning from the hosted UI with an auth code.
  if (params.has('code') && params.has('state')) {
    const user = await mgr.signinCallback();
    if (user?.access_token) setSessionCookie(user.access_token);
    const returnTo = safeReturnPath((user?.state as { returnTo?: string } | undefined)?.returnTo);
    if (returnTo && !returnTo.startsWith('/?')) {
      window.location.replace(returnTo);
      return navigating();
    }
    window.history.replaceState(null, '', '/');
    return;
  }

  let user = await mgr.getUser();
  if (user && user.expired) {
    try {
      user = await mgr.signinSilent();
    } catch {
      user = null;
    }
  }

  if (!user?.access_token) {
    await mgr.signinRedirect({
      state: { returnTo: safeReturnPath(params.get('return')) ?? undefined },
    });
    return navigating();
  }

  setSessionCookie(user.access_token);

  // Valid session but another app sent us here to refresh the cookie —
  // bounce straight back.
  const returnTo = safeReturnPath(params.get('return'));
  if (returnTo) {
    window.location.replace(returnTo);
    return navigating();
  }
}

/**
 * Sign-in for a hub-issued session.
 *
 * Asks the SERVER whether there is a session rather than inspecting anything
 * local, because the session cookie is HttpOnly and so is invisible here by
 * design. That is the right shape anyway: the only opinion that matters about
 * whether a session is valid is the one held by the thing that will answer the
 * next request.
 *
 * An INDETERMINATE answer shows the form. The first version of this rendered
 * the app instead, reasoning that a network blip should not throw someone at a
 * login screen — and a live test proved that backwards. A cross-origin failure
 * is indistinguishable from an offline one here, so the app rendered against a
 * server it could not authenticate to and produced a wall of 401s with nothing
 * on screen to explain them. The form is the better answer either way: if the
 * server is genuinely unreachable, submitting it says so in one sentence, which
 * is more than the empty worklist managed.
 */
async function ensurePasswordSession(early?: SessionProbe): Promise<void> {
  // A token in the URL means the person arrived from an emailed link, and those
  // screens must render WITHOUT a session — confirming an address or resetting
  // a forgotten password are precisely the things you cannot be signed in for.
  // Checked before the session probe so a stale cookie cannot swallow the link.
  const fromEmail = screenFromUrl();
  if (!fromEmail || fromEmail.kind === 'signUp') {
    // The answer boot already asked for, when it got one; asked again when it
    // could not be put (the server may have come up since).
    const asked = early ? await early : null;
    try {
      if (asked ? asked.me : await currentSession()) return;
    } catch {
      // Fall through to the form.
    }
  }

  // Its own React root, mounted into the same element the app will use. The
  // gate resolves only after a successful sign-in, so main.tsx renders the
  // application over the top of this with the session already established.
  return new Promise<void>((resolve) => {
    const el = document.getElementById('root');
    if (!el) return resolve();
    const root = createRoot(el);
    // No StrictMode, matching main.tsx — its double-mount is what forced the
    // application root to drop it, and having one root here behave differently
    // is a difference to explain rather than a benefit.
    root.render(
      <AuthScreens
        initial={fromEmail ?? { kind: 'signIn' }}
        onSignedIn={() => {
          // Unmount before resolving, and asynchronously: React refuses to
          // unmount a root from inside its own render cycle, and the warning it
          // prints instead is easy to miss while the app renders anyway.
          setTimeout(() => {
            root.unmount();
            // The dashboard is a separate app on this origin with no login of
            // its own; it bounces here with ?return= when it hits a 401. Same
            // contract as the OIDC path, parsed by the same function — which
            // resolves the value with the URL parser rather than matching a
            // prefix, because `/\evil.com` passes a startsWith('//') test and
            // still navigates off-origin.
            const params = new URLSearchParams(window.location.search);
            const returnTo = safeReturnPath(params.get('return'));
            if (returnTo) {
              window.location.replace(returnTo);
              return; // navigating away; never resolve, never render
            }
            // Signed in from an emailed link: that URL is /verify or /reset
            // with a spent token in the query, which is not a page the app can
            // render and not one worth keeping in history. Send them to the
            // app's root instead.
            if (fromEmail && fromEmail.kind !== 'signUp') {
              window.location.replace(import.meta.env.BASE_URL);
              return;
            }
            if (fromEmail) window.history.replaceState(null, '', window.location.pathname);
            resolve();
          }, 0);
        }}
      />,
    );
  });
}

/**
 * The screen an emailed link is asking for, or null for an ordinary visit.
 *
 * The last path segment identifies it, so this works wherever the bundle is
 * mounted (/verify and /reset at the root, <base>/verify under a prefix). The
 * server builds these addresses from the same base path it is given at deploy
 * time.
 */
function screenFromUrl(): Screen | null {
  const params = new URLSearchParams(window.location.search);
  if (params.get('signup') === '1') return { kind: 'signUp' };
  const token = params.get('token');
  if (!token) return null;
  const last = window.location.pathname.replace(/\/+$/, '').split('/').pop();
  // The invitation is the one emailed link left (verification and reset are
  // six digit codes typed into the page), and it can be opened by somebody
  // with no account at all — which is the point of it.
  if (last === 'invite') return { kind: 'invite', token };
  return null;
}
